import {
  Card,
  Heading,
  SimpleGrid,
  Box,
  Container,
  Text,
  VStack,
  List,
  Icon,
  Flex,
  Badge,
  Button,
  Spacer,
} from '@chakra-ui/react';
import { NavLink, useLoaderData } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import { useParams } from 'react-router-dom';
import { Product } from '@/lib/types';
import WorkspaceLayout from './workspace-layout';
import api from '@/utils/api';
import useWorkspace from '@/lib/hooks/use-workspace';
import useCurrentUser from '@/lib/hooks/use-current-user';
import useIsCancelled from '@/lib/hooks/use-is-cancelled';

export default function ListProduct() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const products = useLoaderData() as Product[];
  const { workspace } = useWorkspace();
  const { user } = useCurrentUser();
  const isCancelled = useIsCancelled();

  async function getBilling() {
    const response = await api.get(
      `/workspace/subscriptions/portal/${workspace?.stripe_customer_id}`,
    );
    const data = await response.data.data;
    if (data.url) {
      const paymentMethodUrl = !user.has_payment_methods ? '/payment-methods' : '';
      window.open(data.url + paymentMethodUrl);
    }
  }

  return (
    <WorkspaceLayout>
      <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
        <Card.Header>
          <Container position={'relative'} maxW={'3xl'}>
            <Card.Title>
              <Flex>
                <Heading size='3xl'>Billing</Heading>
                <Spacer />
                <Button colorPalette='brand' onClick={getBilling}>
                  {!user?.has_payment_methods ? 'Add Payment Method' : 'Billing Portal'}
                </Button>
              </Flex>
            </Card.Title>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW={'3xl'} mt={6}>
            <SimpleGrid columns={{ base: 1, md: 2 }} gap={10}>
              {products.map((product) => (
                <Card.Root
                  key={product.id}
                  borderWidth={'1px'}
                  borderRadius={'xl'}
                  borderColor={'gray.200'}
                >
                  <Card.Header>
                    <Heading size={'lg'}>
                      {product.name}{' '}
                      <Badge ml={1} fontSize={'md'} colorScheme='green'>
                        {`${product?.price?.usage_count}`}{' '}
                        <Text display={'inline'} fontSize={'xs'} textTransform={'lowercase'}>
                          user(s)
                        </Text>
                      </Badge>
                    </Heading>
                  </Card.Header>
                  <Card.Body py={5}>
                    <VStack gap={5} align={'start'}>
                      <Text fontSize={'xs'}>{product.description}</Text>
                      <Box>
                        <Flex flex='1' gap='4' alignItems='center' flexWrap='wrap'>
                          <Text fontSize={'3xl'} fontWeight={'bold'}>
                            CA$
                            {product?.price?.unit_amount && product?.price?.unit_amount / 100}
                          </Text>
                          <Box>
                            <Text fontSize={'xs'} color={'gray.600'}>
                              per unit
                            </Text>
                            <Text fontSize={'xs'} color={'gray.600'}>
                              per month
                            </Text>
                          </Box>
                        </Flex>
                        <Text fontSize={'xs'} color={'gray.600'}>
                          Billed monthly based on usage
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize={'sm'} mb={2}>
                          This includes:
                        </Text>
                        <List.Root variant='plain' align='center' gap={2} fontSize={'xs'}>
                          {product.marketing_features?.map((feature, index) => (
                            <List.Item key={`feature-${index}`}>
                              <List.Indicator asChild color='green.500'>
                                <Icon as={CheckCircleIcon} w={4} h={4} />
                              </List.Indicator>
                              {feature.name}
                            </List.Item>
                          ))}
                        </List.Root>
                      </Box>
                    </VStack>
                  </Card.Body>
                </Card.Root>
              ))}
            </SimpleGrid>
            {user?.has_payment_methods && (
              <Box pt={8}>
                {!isCancelled && (
                  <Button colorPalette='brand' w={'full'} variant='subtle' asChild>
                    <NavLink to={`/${workspaceId}/cancel-subscription`}>
                      Cancel Subscription
                    </NavLink>
                  </Button>
                )}
                {isCancelled && (
                  <Button colorPalette='brand' w={'full'} asChild>
                    <NavLink to={`/${workspaceId}/resume-subscription`}>
                      Resume Subscription
                    </NavLink>
                  </Button>
                )}
              </Box>
            )}
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}
